import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SpiritualDashboard from '../components/SpiritualDashboard';
import ConsultCard from '../components/ConsultCard';
import PromoBanner from '../components/PromoBanner';

const HABILIDADES_ITEMS = [
  { id: 'astrologia',   name: 'Astrologia Psicológica',  icon: '🌟', route: '/astrologia' },
  { id: 'numerologia',  name: 'Numerologia Pitagórica',  icon: '🔢', route: '/numerologia' },
  { id: 'kabbalah',     name: 'Kabbalah',                icon: '✡️', route: '/kabbalah' },
  { id: 'jung',         name: 'Psicologia & Jung',       icon: '🧠', route: '/jung' },
  { id: 'amor',         name: 'Amor e Relacionamentos',  icon: '💕', route: '/amor' },
  { id: 'prosperidade', name: 'Prosperidade e Propósito',icon: '✨', route: '/prosperidade' },
  { id: 'bem-estar',    name: 'Bem-Estar Emocional',     icon: '🌸', route: '/bem-estar' },
  { id: 'karma',        name: 'Karma e Destino',         icon: '🔮', route: '/karma' },
  { id: 'caminhos',     name: 'Caminhos Espirituais',    icon: '🌙', route: '/caminhos' },
];

// "Pergunta Livre" fica fora do grid de Habilidades (catch-all, não é uma
// habilidade temática), mas custa o mesmo 1 token via /aurora-question.
const OUTRAS_FERRAMENTAS_ITEMS = [
  { id: 'aurora',       name: 'Pergunta Livre',          icon: '💬', route: '/aurora' },
];

const DashboardPage: React.FC = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    refresh().catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gradient-mystical theme-bg-background">

      <PromoBanner />

      {/* Dashboard Espiritual */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-8">
        <SpiritualDashboard />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">

        {/* Destaque — Tarot */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/tarot')}
            className="w-full flex items-center gap-5 px-6 py-5 rounded-2xl transition-all duration-200 hover:scale-[1.01] text-left"
            style={{
              background: 'linear-gradient(135deg, rgba(124,58,237,0.30) 0%, rgba(139,92,246,0.18) 100%)',
              border: '1px solid rgba(139,92,246,0.45)',
            }}
          >
            <span className="text-4xl flex-shrink-0">🃏</span>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg leading-tight">Tarot Rider-Waite</h3>
              <p className="text-white/50 text-sm mt-0.5">9 tiragens disponíveis — Cruz Celta, Mandala Astrológica, Templo de Afrodite e mais</p>
            </div>
            <span className="text-white/40 text-lg flex-shrink-0">→</span>
          </button>
        </div>

        {/* Destaque — Baralho Cigano */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/baralho-cigano')}
            className="w-full flex items-center gap-5 px-6 py-5 rounded-2xl transition-all duration-200 hover:scale-[1.01] text-left"
            style={{
              background: 'linear-gradient(135deg, rgba(21,128,61,0.30) 0%, rgba(74,222,128,0.18) 100%)',
              border: '1px solid rgba(74,222,128,0.45)',
            }}
          >
            <span className="text-4xl flex-shrink-0">🍀</span>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg leading-tight">Baralho Cigano</h3>
              <p className="text-white/50 text-sm mt-0.5">Leitura direta e sem rodeios — Carta Única ou Situação, Ação e Resultado</p>
            </div>
            <span className="text-white/40 text-lg flex-shrink-0">→</span>
          </button>
        </div>

        {/* Destaque — Baralho Sibila */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/baralho-sibilla')}
            className="w-full flex items-center gap-5 px-6 py-5 rounded-2xl transition-all duration-200 hover:scale-[1.01] text-left"
            style={{
              background: 'linear-gradient(135deg, rgba(180,83,9,0.30) 0%, rgba(245,158,11,0.18) 100%)',
              border: '1px solid rgba(245,158,11,0.45)',
            }}
          >
            <span className="text-4xl flex-shrink-0">🎴</span>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg leading-tight">Baralho Sibila</h3>
              <p className="text-white/50 text-sm mt-0.5">Vera Sibilla Italiana — leitura em pares, direta e sem rodeios</p>
            </div>
            <span className="text-white/40 text-lg flex-shrink-0">→</span>
          </button>
        </div>

        {/* Grid de habilidades + acesso rápido */}
        <div className="mb-4">
          <h2 className="text-lg font-bold text-white mb-1">Consultas e Ferramentas</h2>
          <p className="text-white/40 text-sm">Escolha uma habilidade ou ferramenta para começar</p>
        </div>

        {/* Bloco: Habilidades da Aurora — cada consulta custa 1 token */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wide mb-3">Habilidades da Aurora</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {HABILIDADES_ITEMS.map(item => (
              <ConsultCard
                key={item.id}
                icon={item.icon}
                title={item.name}
                to={item.route}
                cost={1}
                balance={user?.tokens_balance}
              />
            ))}
          </div>
        </div>

        {/* Bloco: Outras Ferramentas */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wide mb-3">Outras Ferramentas</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {OUTRAS_FERRAMENTAS_ITEMS.map(item => (
              <ConsultCard
                key={item.id}
                icon={item.icon}
                title={item.name}
                to={item.route}
                cost={1}
                balance={user?.tokens_balance}
              />
            ))}
          </div>
        </div>

        {/* Sinastria e Horóscopo são destinos, não consultas — navegação leve, sem custo em token aqui */}
        <div className="flex flex-wrap gap-2">
          <Link
            to="/profile?tab=conexoes"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            ☯️ Sinastria
          </Link>
          <Link
            to="/horoscope"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            ⭐ Horóscopo do Dia
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
